import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, getToken, setToken, clearToken } from './api';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  refreshUser: async () => {},
  setUser: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }
    // Try up to 3 times with backoff so a flaky network during page reload
    // (the most common cause of the spurious "logged out" behaviour) doesn't
    // kick the user back to /login. Only force-logout on a real 401/403.
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const data = await api('/users/me');
        setUser(data.user || data);
        return;
      } catch (err) {
        lastErr = err;
        if (err?.status === 401 || err?.status === 403) {
          clearToken();
          setUser(null);
          return;
        }
        // Transient — wait then retry
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    // After 3 attempts on a non-auth failure, leave the token in place so
    // the user can manually retry instead of being silently logged out.
    console.warn('refreshUser failed after retries:', lastErr?.status, lastErr?.message);
    setUser(null);
  }, []);

  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  async function login(token, userData) {
    setToken(token);
    setUser(userData);
  }

  function logout() {
    clearToken();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
